import { assertNever } from "@p0security/util/assertNever";

import type { Auth0ConnectorHosting } from "./types.ts";

/** The identity a connector runs as, which each tenant lets read its client
 * secret. */
export type ConnectorIdentity =
  | {
      type: "gcp";
      projectId: string;
      serviceAccountEmail: string;
      serviceAccountId: string;
    }
  | { type: "aws"; accountId: string; roleArn: string; roleName: string };

/**
 * The identity of the connector deployed at `hosting`: an IAM role, or a
 * service account, named after the connector in the connector's own account
 * or project.
 *
 * P0 doesn't look the identity up. The connector's Terraform creates it by
 * this name, and a connector the customer deploys another way must run as an
 * identity of this name too, or its tenants' grants reach nothing.
 *
 * It trims each value, as P0 stores it. P0 takes only regions in AWS's
 * commercial partition, so the partition is `aws`.
 */
export const toConnectorIdentity = (
  hosting: Auth0ConnectorHosting
): ConnectorIdentity => {
  const connectorName = hosting.connectorName.trim();
  switch (hosting.type) {
    case "aws": {
      const accountId = hosting.accountId.trim();
      return {
        accountId,
        roleArn: `arn:aws:iam::${accountId}:role/${connectorName}`,
        roleName: connectorName,
        type: "aws",
      };
    }
    case "gcp": {
      const projectId = hosting.projectId.trim();
      return {
        projectId,
        serviceAccountEmail: `${connectorName}@${projectId}.iam.gserviceaccount.com`,
        serviceAccountId: connectorName,
        type: "gcp",
      };
    }
    default:
      throw assertNever(hosting);
  }
};
